import { ArchivedRecord, useArchived } from "@/ui/ArchivedRecord";
import { ObjectReadGuard } from "@/ui/ObjectReadGuard";
import { QualifyDialog, RequirementsSection } from "@/views/CandidateRequirements";
import { HrbpApplicationRecord } from "@/views/HrbpApplication";
import { AssessmentChain } from "@/views/plan/AssessmentChain";
import { STAGE_LABEL } from "@/ui/vocabulary";
import { useEffect, useMemo, useRef, useState } from "react";
import { replaceHash, useRoute } from "@/router";
import {
  candidateAdd,
  candidateAttachResume,
  candidateDecideAdvance,
  candidateBatchAssemble,
  candidateBatchSend,
  candidateCloseAtSource,
  candidateDispositionEarly,
  candidateHold,
  candidateImport,
  candidateList,
  candidateLogMessage,
  candidateMarkInternal,
  candidateOpen,
  candidateReinstate,
  candidateRelease,
  candidateReverseDecision,
  candidateWithdraw,
  type ApplicationCard,
  type HrbpApplicationCard,
  type CandidateCloseAtSourceInput,
} from "@/api/tools";
import { FileField, type FilePhase, FileList, DOCUMENT_TYPES, type FileVersion } from "@/ui/FileField";
import { RecordAfterFact } from "@/ui/RecordAfterFact";
import type { FileUploadOutput } from "@/api/tools";
import { useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { inputLengthHint, inputMaxLength } from "@/api/inputLimits";
import { useViewState } from "@/shell/viewState";
import { DIALOG_LABELS, NAV_ITEM_LABELS } from "@/mc";
import { CANDIDATE_RULES } from "@/views/candidate/labels";
import { ruleCode } from "@/views/candidate/refusals";
import { MISSING, Banner, Dialog, Empty, Field, KV, Pill, ReasonDialog, Receipt, Skeleton, Table, fmtDate, relativeAge } from "@/ui";

// V1-INTERACTIONS §4.6 — Candidates: the pool (import/add), the pipeline by stage, resume batches (assemble → send → the Hiring
// Manager's decisions), holds, withdrawals and the early disposition. Identity by projection (Law 9): the owner and the Hiring
// Manager see names; the HR Business Partner sees tokens and counts.

const LIVE_COLUMNS: [string, string][] = ["sourced", "qualified", "in_review", "screen", "hm_round", "panel", "offer"].map(key => [key, STAGE_LABEL[key] ?? key]);
const STAGE_TONE: Record<string, "ok" | "warn" | "bad" | "info" | "muted"> = { sourced: "muted", qualified: "info", in_review: "warn", held: "warn", screen: "info", screened: "info", hm_round: "info", hm_assessed: "info", panel: "info", debrief: "info", offer_approved: "ok", offer: "ok", offer_accepted: "ok", hris_hired: "ok", hired: "ok", not_selected: "muted", passed_on: "muted", withdrawn: "muted", declined: "bad", reneged: "bad", dispositioned: "muted", alternate: "warn" };
const SOURCE_LABEL: Record<string, string> = { applied: "Applied", sourced: "Sourced", referral: "Referral", internal: "Internal", agency: "Agency" };
const HOLDER_LABEL: Record<string, string> = { recruiter: "Recruiter", hm: "Hiring Manager", candidate: "Candidate" };
const REASON_CODES = ["scope_mismatch", "experience_gap", "location", "comp_out_of_range", "not_a_fit_for_level", "internal_policy", "other"] as const;
const REASON_LABEL: Record<string, string> = { scope_mismatch: "Scope mismatch", experience_gap: "Experience gap", location: "Location", comp_out_of_range: "Comp out of range", not_a_fit_for_level: "Not a fit for the level", internal_policy: "Internal policy", other: "Other" };

type ReasonCode = CandidateCloseAtSourceInput["reason_code"];

type PipelineCard = ApplicationCard | HrbpApplicationCard;
function name(c: { token: string; display_name?: string | null }) {
  return c.display_name ?? `Candidate ${c.token.slice(-4).toUpperCase()}`;
}

// ---- the pipeline tab ---------------------------------------------------------------------------------------------------------

export function CandidatesTab({ id, allowAdd }: { id: string; allowAdd: boolean }) {
  const archived = useArchived();
  const { me, can, manifest } = useSession();
  const nameLimit = inputMaxLength(manifest, "candidate.add", "display_name");
  const q = useRead(["candidates", id], () => candidateList({ requisition_id: id }));
  const inval = [["candidates", id], ["requisition", id], ["requisitions"], ["today"]] as const;
  const add = useWrite((i: Parameters<typeof candidateAdd>[0], k) => candidateAdd(i, k), inval);
  const imp = useWrite((i: Parameters<typeof candidateImport>[0], k) => candidateImport(i, k), inval);
  const closeSrc = useWrite((i: Parameters<typeof candidateCloseAtSource>[0], k) => candidateCloseAtSource(i, k), inval);
  const assemble = useWrite((i: { requisition_id: string; reads: Record<string, unknown>[] }, k) => candidateBatchAssemble(i, k), inval);
  const send = useWrite((i: { requisition_id: string; application_ids: string[] }, k) => candidateBatchSend(i, k), inval);
  const [dlg, setDlg] = useState<null | { kind: "add" } | { kind: "import" } | { kind: "qualify"; app: PipelineCard } | { kind: "close"; app: PipelineCard } | { kind: "batch" }>(null);
  const [form, setForm] = useState({ display_name: "", source_channel: "sourced" as "applied" | "sourced" | "referral" | "internal" | "agency", ats_id: "", email: "" });
  const [resume, setResume] = useState<FileUploadOutput | null>(null);
  const [uploading, setUploading] = useState(false);
  const [csv, setCsv] = useState("");
  const [preview, setPreview] = useState<{ imported: Record<string, unknown>[]; skipped: Record<string, unknown>[] } | null>(null);
  const [reads, setReads] = useState<Record<string, { note: string; below: string; include: boolean }>>({});
  const [closeCode, setCloseCode] = useState<ReasonCode>("scope_mismatch");
  const [batchReceipt, setBatchReceipt] = useState<string | null>(null);
  const data = q.data;
  const isOwner = !archived && data?.state_archive !== "archived" && data?.projection === "identity";
  const isHm = data?.projection === "review";
  const route = useRoute();
  const requestedAction = new URLSearchParams(route.raw.split("?")[1]).get("action");
  const openedAction = useRef<string | null>(null);
  useEffect(() => {
    if (!requestedAction) { openedAction.current = null; return; }
    if (!isOwner || !allowAdd || openedAction.current === requestedAction) return;
    if ((requestedAction === "add" && can("candidate.add")) || (requestedAction === "import" && can("candidate.import"))) {
      openedAction.current = requestedAction;
      setDlg({ kind: requestedAction });
      replaceHash(`requisitions/${id}/candidates`);
    }
  }, [requestedAction, isOwner, allowAdd, can, id]);
  const byStage = useMemo(() => {
    const m: Record<string, PipelineCard[]> = {};
    for (const c of data?.items ?? []) (m[c.stage] ??= []).push(c);
    return m;
  }, [data]);
  const qualified = byStage["qualified"] ?? [];
  const closed = (data?.items ?? []).filter((c) => !LIVE_COLUMNS.some(([k]) => k === c.stage) && !["held", "screened", "hm_assessed", "debrief", "offer_approved", "alternate"].includes(c.stage));
  const others = (data?.items ?? []).filter((c) => ["held", "screened", "hm_assessed", "debrief", "offer_approved", "alternate"].includes(c.stage));
  const rows = (csvText: string) =>
    csvText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean)
      .filter((l) => !/^ats_id\s*,/i.test(l))
      .map((l) => {
        const [ats_id = "", display_name = "", email = "", source = ""] = l.split(",").map((s) => s.trim());
        return { ats_id, display_name, email: email || null, source_channel: (["applied", "sourced", "referral", "internal", "agency"].includes(source) ? source : "applied") as "applied" };
      })
      .filter((r) => r.ats_id && r.display_name);
  if (q.error || !data) return <ObjectReadGuard query={q} />;
  return (
    <div className="pipeline">
      <RecordAfterFact id={id} family="requisition" section="candidates" />
      {/* lane B3: the requisition's board in the design system (§5 c: one link row; this tab stays V1 until B2 mounts the board) */}
      <p className="muted"><a href={`#pipeline/${id}`}>{NAV_ITEM_LABELS.pipeline}</a></p>
      <div className="hrow">
        <div>
          <h2 className="sec">
            Pipeline <span className="sec__n">{data.items.length}</span>
          </h2>
          <p className="muted">
            {data.projection === "hrbp" ? "Tokens and counts — identity appears where the task is authorised." : data.projection === "review" ? "Candidates from the first batch onward; decide each batch within three business days." : "Every application progresses on its own; one candidate never blocks another."}
          </p>
        </div>
        {isOwner && (
          <div className="acts">
            {can("candidate.import") && (
              <button className="btn" onClick={() => setDlg({ kind: "import" })}>
                Import
              </button>
            )}
            {can("candidate.add") && (
              <button className="btn" onClick={() => { setResume(null); setUploading(false); setDlg({ kind: "add" }); }}>
                Add candidate
              </button>
            )}
            {can("candidate.batch_send") && (
              <button className="btn btn--primary" disabled={qualified.length === 0} aria-disabled={qualified.length === 0} aria-describedby={qualified.length === 0 ? "batch-blocker" : undefined} title={qualified.length === 0 ? "Qualify a candidate first" : ""} onClick={() => { setReads(Object.fromEntries(qualified.map((c) => [c.id, { note: ("recruiter_note" in c ? c.recruiter_note : null) ?? "", below: "", include: true }]))); setDlg({ kind: "batch" }); }}>
                Send a batch{qualified.length ? ` (${qualified.length})` : ""}
              </button>
            )}
          </div>
        )}
      </div>
      {isOwner && can("candidate.batch_send") && qualified.length === 0 && <p id="batch-blocker" className="field__hint">Qualify a candidate before sending a batch.</p>}
      {batchReceipt && <Receipt>{batchReceipt}</Receipt>}
      <Banner error={closeSrc.error ?? send.error ?? assemble.error} />
      {"batches" in data && data.batches.length > 0 && (
        <div className="batches">
          {data.batches.map((b) => (
            <a key={String(b["batch_no"])} className="batch" href={`#requisitions/${id}/batches/${String(b["batch_no"])}`}>
              <b>Batch {String(b["batch_no"])}</b>
              <span>
                {String(b["count"])} {Number(b["count"]) === 1 ? "candidate" : "candidates"} · {Number(b["undecided"]) > 0 ? <Pill tone="warn">{String(b["undecided"])} undecided</Pill> : <Pill tone="ok">decided</Pill>}
              </span>
            </a>
          ))}
        </div>
      )}
      {data.items.length === 0 ? (
        <Empty heading="No candidates yet" action={isOwner ? <div className="acts">{can("candidate.import") && <button className="btn" onClick={() => setDlg({ kind: "import" })}>Import from the ATS</button>}{can("candidate.add") && <button className="btn btn--primary" onClick={() => { setResume(null); setUploading(false); setDlg({ kind: "add" }); }}>Add candidate</button>}</div> : undefined}>{isOwner ? "No candidates yet — import from the ATS or add one by hand once the position is posted." : isHm ? "The first resume batch lands here." : "No candidates yet."}</Empty>
      ) : (
        <div className="board board--stages" role="list" aria-label="Pipeline by stage">
          {LIVE_COLUMNS.map(([k, label]) => (
            <section key={k} className={`col${!(byStage[k] ?? []).length ? " board__col--empty" : ""}`} role="listitem" aria-label={label}>
              <h3>
                {label} <span className="sec__n">{(byStage[k] ?? []).length}</span>
              </h3>
              {!(byStage[k] ?? []).length && <p className="muted">No candidates</p>}
              {(byStage[k] ?? []).map((c) => (
                <CandidateCard key={c.id} c={c} rid={id} onQualify={() => setDlg({ kind: "qualify", app: c })} onClose={() => setDlg({ kind: "close", app: c })} />
              ))}
            </section>
          ))}
        </div>
      )}
      {others.length > 0 && (
        <section>
          <h3 className="sec">Held, assessed and waiting</h3>
          <div className="cards">
            {others.map((c) => (
              <CandidateCard key={c.id} c={c} rid={id} />
            ))}
          </div>
        </section>
      )}
      {closed.length > 0 && (
        <section>
          <h3 className="sec">
            Closed <span className="sec__n">{closed.length}</span>
          </h3>
          <Table
            label="Closed applications"
            head={
              <tr>
                <th className="sticky-l">Candidate</th>
                <th>Stage</th>
                <th>Furthest</th>
                {data.projection !== "hrbp" && <th>Disposition</th>}
                <th>Since</th>
              </tr>
            }
          >
            {closed.map((c) => (
              <tr key={c.id}>
                <td className="sticky-l">
                  <a href={`#requisitions/${id}/candidates/${c.id}`}>{name(c)}</a>
                </td>
                <td>
                  <Pill tone={STAGE_TONE[c.stage] ?? "muted"}>{c.stage_label}</Pill>
                </td>
                <td className="muted nowrap">{c.furthest_stage_label}</td>
                {"disposition_status" in c && <td>{c.disposition_status === "obligation_open" ? <Pill tone="warn">code due</Pill> : c.disposition_status === "recorded" ? <Pill tone="ok">recorded</Pill> : MISSING}</td>}
                <td className="muted nowrap">{fmtDate(c.stage_entered_at)}</td>
              </tr>
            ))}
          </Table>
        </section>
      )}

      <Dialog title="Add a candidate" open={dlg?.kind === "add"} onClose={() => setDlg(null)}>
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void add.mutateAsync({ requisition_id: id, display_name: form.display_name.trim(), source_channel: form.source_channel, ats_id: form.ats_id.trim() || null, email: form.email.trim() || null, resume_file_id: resume?.file_id ?? null }).then(() => { setDlg(null); setResume(null); setForm({ display_name: "", source_channel: "sourced", ats_id: "", email: "" }); });
          }}
        >
          <Field label="Name" required id="c-name" error={add.error?.fieldErrors.display_name} hint={inputLengthHint(form.display_name, nameLimit)}>
            <input id="c-name" value={form.display_name} maxLength={nameLimit} onChange={(e) => setForm({ ...form, display_name: e.target.value })} required />
          </Field>
          <Field label="Source" required id="c-src">
            <select id="c-src" value={form.source_channel} onChange={(e) => setForm({ ...form, source_channel: e.target.value as typeof form.source_channel })}>
              {Object.entries(SOURCE_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </Field>
          <Field label="ATS reference" id="c-ats" hint="Optional; shows as unverified until the ATS confirms it. Duplicates are matched on this id only.">
            <input id="c-ats" value={form.ats_id} onChange={(e) => setForm({ ...form, ats_id: e.target.value })} />
          </Field>
          <Field label="Email" id="c-email">
            <input id="c-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          {dlg?.kind === "add" && <FileField label="Résumé" owner={{ owner_type: "requisition", owner_id: id, requisition_id: id, kind: "resume" }} accept={DOCUMENT_TYPES} onChange={setResume} onBusy={setUploading} />}
          <p id="add-blocker" className="field__hint">{uploading ? "Wait for the résumé upload to finish." : !form.display_name.trim() ? "Enter the candidate’s name to add them." : "Ready to add the candidate."}</p>
          {add.error && ruleCode(add.error) === "potential_matches" ? (
            // lane B3: the duplicate gate refuses before save; the candidate master's Add candidate shows the matches
            <p className="field__hint" role="alert">{CANDIDATE_RULES.potentialMatch} <a href={`#candidates?action=add&req=${encodeURIComponent(id)}`}>{DIALOG_LABELS.addCandidate}</a></p>
          ) : <Banner error={add.error} />}
          <div className="acts">
            <button type="button" className="btn" onClick={() => setDlg(null)}>
              Cancel
            </button>
            <button className="btn btn--primary" aria-disabled={uploading || add.isPending || !form.display_name.trim()} aria-describedby="add-blocker" disabled={uploading || add.isPending || !form.display_name.trim()}>
              Add
            </button>
          </div>
        </form>
      </Dialog>

      <Dialog title="Import candidates" open={dlg?.kind === "import"} onClose={() => { setDlg(null); setPreview(null); }}>
        <p className="muted">Paste CSV rows as ATS reference, name, email, source. Preview marks duplicates: already in this pipeline (skipped) or in another loop (imported with a note).</p>
        <Field label="Rows" id="c-csv" required>
          <textarea id="c-csv" rows={6} value={csv} onChange={(e) => { setCsv(e.target.value); setPreview(null); }} placeholder={"ATS-1001, Avery Stone, avery@example.com, applied"} />
        </Field>
        {preview && (
          <div className="preview">
            <b>{preview.imported.length} to import · {preview.skipped.length} skipped</b>
            <ul>
              {preview.imported.map((r) => (
                <li key={String(r["ats_id"])}>
                  {String(r["display_name"])} <span className="mono">{String(r["ats_id"])}</span>
                  {r["duplicate"] ? <Pill tone="warn">{String((r["duplicate"] as Record<string, unknown>)["kind"]).replace(/_/g, " ")}</Pill> : null}
                </li>
              ))}
              {preview.skipped.map((r) => (
                <li key={`s-${String(r["ats_id"])}`} className="muted">
                  <span className="mono">{String(r["ats_id"])}</span> — {String(r["reason"])}
                </li>
              ))}
            </ul>
          </div>
        )}
        <Banner error={imp.error} />
        <div className="acts">
          <button type="button" className="btn" onClick={() => void imp.mutateAsync({ requisition_id: id, source: "csv", rows: rows(csv), preview: true }).then((r) => setPreview({ imported: r.imported, skipped: r.skipped }))} disabled={imp.isPending || rows(csv).length === 0}>
            Preview
          </button>
          <button type="button" className="btn btn--primary" disabled={imp.isPending || !preview} onClick={() => void imp.mutateAsync({ requisition_id: id, source: "csv", rows: rows(csv), preview: false }).then((r) => { setDlg(null); setPreview(null); setCsv(""); setBatchReceipt(`${r.imported.length} imported · ${r.skipped.length} skipped`); })}>
            Import
          </button>
        </div>
      </Dialog>

      {dlg?.kind === "qualify" && <QualifyDialog rid={id} aid={dlg.app.id} name={name(dlg.app)} onClose={() => setDlg(null)} />}
      <ReasonDialog title={`Close at source — ${dlg?.kind === "close" ? name(dlg.app) : ""}`} label="Note (optional)" optional confirm="Close" open={dlg?.kind === "close"} onClose={() => setDlg(null)} busy={closeSrc.isPending} error={closeSrc.error} onConfirm={(note) => dlg?.kind === "close" && void closeSrc.mutateAsync({ requisition_id: id, application_id: dlg.app.id, reason_code: closeCode, note: note || null }).then(() => setDlg(null))}>
        <Field label="Reason code" required id="c-code" hint="The disposition obligation opens now: code and date within the level's window">
          <select id="c-code" value={closeCode} onChange={(e) => setCloseCode(e.target.value as ReasonCode)}>
            {REASON_CODES.map((k) => (
              <option key={k} value={k}>
                {REASON_LABEL[k]}
              </option>
            ))}
          </select>
        </Field>
      </ReasonDialog>

      <Dialog title="Send a resume batch" open={dlg?.kind === "batch"} onClose={() => setDlg(null)}>
        <p className="muted">One line per candidate: your read against the must-haves. A candidate below the bar needs a reason. The first batch stamps the Sourcing checkpoint; the Hiring Manager decides within three business days.</p>
        {qualified.map((c) => {
          const r = reads[c.id] ?? { note: "", below: "", include: true };
          return (
            <div key={c.id} className={`read${r.include ? "" : " read--off"}`}>
              <label className="check">
                <input type="checkbox" checked={r.include} onChange={(e) => setReads({ ...reads, [c.id]: { ...r, include: e.target.checked } })} /> <b>{name(c)}</b> <span className="muted">{"source_channel" in c ? SOURCE_LABEL[c.source_channel] ?? c.source_channel : ""}</span>
              </label>
              <Field label="Read" required id={`rd-${c.id}`}>
                <input id={`rd-${c.id}`} value={r.note} onChange={(e) => setReads({ ...reads, [c.id]: { ...r, note: e.target.value } })} disabled={!r.include} />
              </Field>
              <Field label="Below the bar because" id={`bb-${c.id}`} hint="Leave empty when the candidate meets the must-haves">
                <input id={`bb-${c.id}`} value={r.below} onChange={(e) => setReads({ ...reads, [c.id]: { ...r, below: e.target.value } })} disabled={!r.include} />
              </Field>
            </div>
          );
        })}
        <Banner error={assemble.error ?? send.error} />
        <p id="batch-send-blocker" className="muted">Include at least one candidate and add their Read note before sending.</p>
        <div className="acts">
          <button type="button" className="btn" onClick={() => setDlg(null)}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            aria-describedby="batch-send-blocker" disabled={assemble.isPending || send.isPending || !Object.values(reads).some((r) => r.include && r.note.trim())}
            onClick={() => {
              const chosen = qualified.filter((c) => reads[c.id]?.include);
              void assemble
                .mutateAsync({ requisition_id: id, reads: chosen.map((c) => ({ application_id: c.id, recruiter_note: reads[c.id]?.note ?? "", ...(reads[c.id]?.below ? { below_the_bar_reason: reads[c.id]?.below } : {}) })) })
                .then(() => send.mutateAsync({ requisition_id: id, application_ids: chosen.map((c) => c.id) }))
                .then((r) => { setDlg(null); setBatchReceipt(`Batch ${r.batch_no} sent — ${r.application_ids.length} candidates with the Hiring Manager.`); });
            }}
          >
            Send batch
          </button>
        </div>
      </Dialog>
      {me?.roles.includes("hrbp") && data.projection === "hrbp" && <p className="muted">Counts by stage: {Object.entries(data.counts).map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`).join(" · ")}</p>}
    </div>
  );
}

function CandidateCard({ c, rid, onQualify, onClose }: { c: PipelineCard; rid: string; onQualify?: (() => void) | undefined; onClose?: (() => void) | undefined }) {
  return (
    <article className="cand">
      <a className="cand__name" href={`#requisitions/${rid}/candidates/${c.id}`}>
        {name(c)}
      </a>
      <div className="cand__meta">
        <span>{"source_channel" in c ? SOURCE_LABEL[c.source_channel] ?? c.source_channel : ""}</span>
        {"ats_id" in c && c.ats_id && <span className="mono">{c.ats_id}{!c.ats_verified && " · unverified"}</span>}
        {"internal" in c && c.internal && <Pill tone="info">internal</Pill>}
        {"batch_no" in c && c.batch_no && <span>batch {c.batch_no}</span>}
      </div>
      {"recruiter_note" in c && c.recruiter_note && <p className="cand__read">{c.recruiter_note}</p>}
      <div className="cand__foot">
        <span className="muted">
          {c.waiting_business_days} bd{"holder" in c && c.holder ? ` · with ${HOLDER_LABEL[c.holder] ?? c.holder}` : ""}
        </span>
        {"hold_reason" in c && c.stage === "held" && <Pill tone="warn">held · {(c.hold_reason ?? "").replace(/_/g, " ")}</Pill>}
        {"disposition_status" in c && c.disposition_status === "obligation_open" && <Pill tone="warn">disposition due</Pill>}
      </div>
      {(c.can["qualify"] || c.can["close_at_source"]) && (
        <div className="cand__acts">
          {c.can["qualify"] && onQualify && (
            <button className="btn btn--sm btn--primary" onClick={onQualify}>
              Qualify
            </button>
          )}
          {c.can["close_at_source"] && onClose && (
            <button className="btn btn--sm" onClick={onClose}>
              Close at source
            </button>
          )}
        </div>
      )}
    </article>
  );
}

// ---- the application record --------------------------------------------------------------------------------------------------

export function ApplicationPage({ rid, aid }: { rid: string; aid: string }) {
  const q = useRead(["application", aid, rid], () => candidateOpen({ requisition_id: rid, application_id: aid }));
  const recordControl = <RecordAfterFact id={rid} family="requisition" section="candidates" references={{ application_id: aid }} />;
  // Resolve the application before mounting its second observer or any write controls.
  return <ObjectReadGuard query={q}><ArchivedRecord record={q.data}><ApplicationRecord rid={rid} aid={aid} recordControl={recordControl} /></ArchivedRecord></ObjectReadGuard>;
}
function ApplicationRecord({ rid, aid, recordControl }: { rid: string; aid: string; recordControl: React.ReactNode }) {
  const { register } = useViewState();
  const { can, me } = useSession();
  const q = useRead(["application", aid, rid], () => candidateOpen({ requisition_id: rid, application_id: aid }));
  useEffect(() => register({ view: "application", ids: { requisition: rid, application: aid }, summary: `application ${aid} ${q.data?.card.stage ?? ""}` }), [register, rid, aid, q.data?.card.stage]);
  const inval = [["application", aid], ["candidates", rid], ["requisition", rid], ["today"]] as const;
  const withdraw = useWrite((i: { requisition_id: string; application_id: string; reason_code: "took_other_role" | "comp" | "location" | "timing" | "no_longer_interested" | "unreachable" | "other"; reason_text?: string | null }, k) => candidateWithdraw(i, k), inval);
  const reinstate = useWrite((i: { requisition_id: string; application_id: string; reason: string }, k) => candidateReinstate(i, k), inval);
  const hold = useWrite((i: { requisition_id: string; application_id: string; reason: "stronger_candidates_first" | "open_question" | "capacity_alternate" | "other"; reason_text?: string | null }, k) => candidateHold(i, k), inval);
  const release = useWrite((i: { requisition_id: string; application_id: string; outcome: "release" | "pass"; reason_text?: string | null }, k) => candidateRelease(i, k), inval);
  const early = useWrite((i: Parameters<typeof candidateDispositionEarly>[0], k) => candidateDispositionEarly(i, k), inval);
  const reverse = useWrite((i: { requisition_id: string; application_id: string; reason: string; told_check: boolean }, k) => candidateReverseDecision(i, k), inval);
  const logMsg = useWrite((i: { requisition_id: string; application_id: string; kind: "invitation" | "itinerary" | "exercise_brief" | "closing_note" | "other"; sent_at: string; reference?: string | null; reply_logged?: boolean }, k) => candidateLogMessage(i, k), inval);
  const internal = useWrite((i: { requisition_id: string; application_id: string; internal: boolean }, k) => candidateMarkInternal(i, k), inval);
  const [dlg, setDlg] = useState<null | "withdraw" | "reinstate" | "hold" | "release" | "pass" | "early" | "reverse" | "message" | "adv_advance" | "adv_hold" | "adv_pass">(null);
  const advance = useWrite((i: Parameters<typeof candidateDecideAdvance>[0], k) => candidateDecideAdvance(i, k), inval);
  const [wCode, setWCode] = useState<"took_other_role" | "comp" | "location" | "timing" | "no_longer_interested" | "unreachable" | "other">("took_other_role");
  const [hCode, setHCode] = useState<"stronger_candidates_first" | "open_question" | "capacity_alternate" | "other">("stronger_candidates_first");
  const [eCode, setECode] = useState<ReasonCode>("scope_mismatch");
  const [told, setTold] = useState(false);
  const [mKind, setMKind] = useState<"invitation" | "itinerary" | "exercise_brief" | "closing_note" | "other">("invitation");
  const [mRef, setMRef] = useState("");
  if (q.error || !q.data) return <ObjectReadGuard query={q} />;
  const a = q.data;
  // the HR Business Partner's projection carries no rounds; `email` is not a discriminator — the registry removes it for
  // a reader below the contact class (absent, never null)
  if (!("rounds" in a)) return <HrbpApplicationRecord rid={rid} aid={aid} application={a} />;
  const c = a.card;
  const actorNames = new Map([[me?.person_id, me?.name]]);
  const history = [...a.stage_history].reverse();
  const historyDays = new Map<string, typeof history>();
  for (const event of history) {
    const day = fmtDate(String(event["at"]));
    const entries = historyDays.get(day) ?? [];
    entries.push(event); historyDays.set(day, entries);
  }
  const err = withdraw.error ?? reinstate.error ?? hold.error ?? release.error ?? early.error ?? reverse.error ?? logMsg.error ?? internal.error;
  return (
    <div>
      <div className="hrow">
        <div>
          <div className="title-line"><h1 className="page">{name(c)}</h1><Pill tone={STAGE_TONE[c.stage] ?? "muted"}>{c.stage_label}</Pill></div>
          <p className="lede">
            <a href={`#requisitions/${rid}/candidates`}>← {rid} · candidates</a> · {"source_channel" in c ? SOURCE_LABEL[c.source_channel] ?? c.source_channel : ""}
            {c.ats_id ? (
              <>
                {" "}
                · ATS <span className="mono">{c.ats_id}</span> {!c.ats_verified && <Pill tone="warn">unverified</Pill>}
              </>
            ) : null}
            {"internal" in c && c.internal && (
              <>
                {" "}
                <Pill tone="info">internal / referral</Pill>
              </>
            )}
          </p>
        </div>
        <div className="acts application-actions">
          {a.state_archive === "live" && c.stage === "hm_assessed" && can("candidate.decide_advance") && (
            <>
              <button className="btn btn--primary" onClick={() => setDlg("adv_advance")}>
                Advance to the panel
              </button>
              <button className="btn" onClick={() => setDlg("adv_hold")}>
                Hold after this round
              </button>
              <button className="btn" onClick={() => setDlg("adv_pass")}>
                Pass after this round
              </button>
            </>
          )}
          <div className="acts action-tools">
          {/* two holds can be offered at once and they are two different writes: `candidate.hold` parks the application
              wherever it stands (any live stage, its own reason, a five-business-day review), while the advance decision
              below holds the candidate as the Hiring Manager's recorded verdict on their own round. Each says its scope. */}
          {c.can["hold"] && (
            <button className="btn btn--quiet" onClick={() => setDlg("hold")}>
              Hold candidate
            </button>
          )}
          {c.can["release"] && (
            <>
              <button className="btn btn--primary" onClick={() => setDlg("release")}>
                Release
              </button>
              <button className="btn" onClick={() => setDlg("pass")}>
                Pass
              </button>
            </>
          )}
          {c.can["withdraw"] && (
            <button className="btn" onClick={() => setDlg("withdraw")}>
              Withdrew
            </button>
          )}
          {c.can["reinstate"] && (
            <button className="btn btn--primary" onClick={() => setDlg("reinstate")}>
              Reinstate
            </button>
          )}
          {c.can["disposition_early"] && c.stage !== "held" && (
            <button className="btn btn--quiet" onClick={() => setDlg("early")}>
              Not selected
            </button>
          )}
          {c.can["reverse"] && (
            <button className="btn btn--quiet" onClick={() => setDlg("reverse")}>
              Reverse last decision
            </button>
          )}
          {c.can["log_message"] && (
            <button className="btn" onClick={() => setDlg("message")}>
              Log a message
            </button>
          )}
          {(c.stage === "screen" || c.stage === "screened") && (
            <a className="btn btn--primary" href={`#requisitions/${rid}/candidates/${aid}/rounds/1`}>
              Recruiter Screen
            </a>
          )}
          {(c.stage === "hm_round" || c.stage === "hm_assessed") && (
            <a className={c.stage === "hm_assessed" ? "btn btn--quiet" : "btn btn--primary"} href={`#requisitions/${rid}/candidates/${aid}/rounds/2`}>
              Hiring Manager interview
            </a>
          )}
          {(c.stage === "panel" || c.stage === "debrief" || c.stage === "offer_approved" || c.stage === "alternate") && (
            <a className="btn" href={`#requisitions/${rid}/summary/${aid}`}>
              Summary Sheet
            </a>
          )}
          {(c.stage === "offer_approved" || c.stage === "offer" || c.stage === "offer_accepted" || c.stage === "hris_hired" || c.stage === "hired" || c.stage === "declined" || c.stage === "reneged") && (
            <a className="btn btn--primary" href={`#requisitions/${rid}/offer/${aid}`}>
              Offer & close
            </a>
          )}
          {recordControl}
          </div>
        </div>
      </div>
      <Banner error={err} />
      <div className="record-grid record-grid--start">
        <section className="card">
          <h2 className="sec">Application</h2>
          <KV
            wide={["In this stage since", "Recruiter's read", "Contact", "Held", "Disposition"].filter((key) => key === "In this stage since" || key === "Recruiter's read" && !!c.recruiter_note || key === "Contact" && !!(a.email || a.phone) || key === "Held" && c.stage === "held" || key === "Disposition" && c.disposition_status === "obligation_open")}
            items={[
              ["Entered the pipeline", fmtDate(c.entered_pipeline_at)],
              ["In this stage since", `${fmtDate(c.stage_entered_at)} · ${c.waiting_business_days} business days${c.holder ? ` · with the ${HOLDER_LABEL[c.holder] ?? c.holder}` : ""}`],
              ["Furthest stage", c.furthest_stage_label],
              ["Batch", c.batch_no ? `Batch ${c.batch_no}` : MISSING],
              ["Recruiter's read", c.recruiter_note ?? MISSING],
              ["Contact", a.email || a.phone ? `${a.email ?? ""} ${a.phone ?? ""}`.trim() : MISSING],
              ["Held", c.stage === "held" ? `${(c.hold_reason ?? "").replace(/_/g, " ")} · from ${(c.held_from_stage ?? "").replace(/_/g, " ")}` : MISSING],
              ["Disposition", c.disposition_status === "obligation_open" ? <Pill tone="warn">code and date due {a.disposition?.["due_at"] ? `by ${fmtDate(String(a.disposition["due_at"]))}` : ""}</Pill> : c.disposition_status === "recorded" ? `recorded · ${String(a.disposition?.["code"] ?? "")}` : MISSING],
            ]}
          />
          <details><summary>Technical details</summary><KV items={[["Candidate token", <span className="mono">{c.token}</span>], ["Résumé file", a.resume_file_id]]} /></details>
          <ResumeSection rid={rid} aid={aid} candidate={c.token} application={a} />
          {a.projection === "identity" && <RequirementsSection rid={rid} aid={aid} name={name(c)} reads={c.requirements_met} canQualify={!!c.can["qualify"] && a.state_archive === "live"} />}
        </section>
        <section className="card">
          <h2 className="sec">Stage history</h2>
          {Array.from(historyDays, ([day, events]) => <div key={day}>
            <h3 className="stage-stepper__day">{day}</h3>
            <ol className="history stage-stepper">
              {events.map((e, i) => (
              <li key={i}>
                <span aria-hidden="true">✓ </span><b>{STAGE_LABEL[String(e["to"])] ?? String(e["to"])}</b><div className="muted">Moved by {String(e["by_name"] ?? actorNames.get(String(e["by"])) ?? e["by"] ?? "Not recorded")} · <time title={fmtDate(String(e["at"]), true)}>{relativeAge(String(e["at"]))}</time></div>
                {e["reason_code"] ? <span className="muted"> · {String(e["reason_code"]).replace(/_/g, " ")}</span> : null}
                {e["reason"] ? <div className="muted">{String(e["reason"])}</div> : null}
              </li>
              ))}
            </ol>
          </div>)}
        </section>
        <section className="card">
          <h2 className="sec">
            Decisions <span className="sec__n">{a.decisions.length}</span>
          </h2>
          {a.decisions.length === 0 ? (
            <Empty heading="Awaiting a decision">No decision yet.</Empty>
          ) : (
            <ol className="history">
              {a.decisions.map((d) => (
                <li key={String(d["id"])}>
                  <b>{String(d["kind"]).replace(/_/g, " ")}: {String(d["value"])}</b> <span className="muted">{fmtDate(String(d["at"]), true)}</span>
                  {d["supersedes"] ? <Pill tone="muted">supersedes</Pill> : null}
                  {d["reason"] ? <div className="muted">{String(d["reason"])}</div> : null}
                </li>
              ))}
            </ol>
          )}
          {a.holds.length > 0 && (
            <>
              <h3 className="sec">Holds</h3>
              <ul className="history">
                {a.holds.map((h) => (
                  <li key={String(h["id"])}>
                    {String(h["reason"]).replace(/_/g, " ")} · {fmtDate(String(h["placed_at"]))} → {h["released_at"] ? `${fmtDate(String(h["released_at"]))} (${String(h["outcome"])})` : `review by ${fmtDate(String(h["review_due_at"]))}`}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
        <section className="card">
          <h2 className="sec">
            Messages logged
          </h2>
          {a.messages.length === 0 ? (
            <Empty heading="No candidate communications yet">Messages to the candidate go through the ATS and are logged here.</Empty>
          ) : (
            <ul className="history">
              {a.messages.map((m) => (
                <li key={String(m["id"])}>
                  <b>{String(m["kind"]).replace(/_/g, " ")}</b> <span className="muted">{fmtDate(String(m["sent_at"]), true)}</span> {m["reference"] ? <span className="mono">{String(m["reference"])}</span> : null} {m["reply_logged_at"] ? <Pill tone="ok">replied</Pill> : String(m["kind"]) === "invitation" ? <Pill tone="warn">awaiting reply</Pill> : null}
                </li>
              ))}

            </ul>
          )}
          {a.withdrawals.length > 0 && (
            <>
              <h3 className="sec">Withdrawals</h3>
              <ul className="history">
                {a.withdrawals.map((w) => (
                  <li key={String(w["id"])}>
                    {String(w["reason_code"]).replace(/_/g, " ")} from {String(w["from"]).replace(/_/g, " ")} · {fmtDate(String(w["at"]))} {w["reinstated_at"] ? <Pill tone="ok">reinstated {fmtDate(String(w["reinstated_at"]))}</Pill> : null}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </div>
      <section style={{ marginTop: 14 }}>
        <h2 className="sec">Rounds</h2>
        <Table label="Rounds" mobile="stack" head={<tr><th>Round</th><th>State</th><th>When</th><th>Interviewer</th><th>Packet status</th><th>Scorecard status</th></tr>}>
          {a.rounds.length === 0 ? <tr><td colSpan={6}>No rounds booked yet.</td></tr> : a.rounds.map((round) => {
            const route = round.round_kind === "recruiter_screen" ? "1" : round.round_kind === "hm_round" ? "2" : round.round_def_id;
            const statusLabel = (value: string) => value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, " ");
            return <tr key={round.id}>
              <td><a href={`#requisitions/${rid}/candidates/${aid}/rounds/${route}`}>{round.label}</a></td>
              <td>{statusLabel(round.status)}</td>
              <td>{round.start_at ? fmtDate(round.start_at, true) : "Not scheduled"}</td>
              <td>{round.interviewer ?? "Not assigned"}</td>
              <td>{round.packet_status ? statusLabel(round.packet_status) : "Not generated"}</td>
              <td>{statusLabel(round.scorecard_status)}</td>
            </tr>;
          })}
        </Table>
      </section>
      <AssessmentChain rid={rid} aid={aid} />
      {c.can["log_message"] && !c.internal && (
        <p className="muted">
          <button className="btn btn--sm" disabled={internal.isPending} onClick={() => void internal.mutateAsync({ requisition_id: rid, application_id: aid, internal: true })}>
            Flag as internal / referral
          </button>{" "}
          The intake's internal-candidate policy then applies; the Hiring Manager and HR Business Partner are told.
        </p>
      )}

      <ReasonDialog title="The candidate withdrew" label="Note (optional)" optional confirm="Record" open={dlg === "withdraw"} onClose={() => setDlg(null)} busy={withdraw.isPending} error={withdraw.error} onConfirm={(t) => void withdraw.mutateAsync({ requisition_id: rid, application_id: aid, reason_code: wCode, reason_text: t || null }).then(() => setDlg(null))}>
        <Field label="Reason" required id="w-code" hint="Booked rounds are cancelled and interviewers told; the candidate_withdrew disposition is written automatically">
          <select id="w-code" value={wCode} onChange={(e) => setWCode(e.target.value as typeof wCode)}>
            {(["took_other_role", "comp", "location", "timing", "no_longer_interested", "unreachable", "other"] as const).map((k) => (
              <option key={k} value={k}>
                {k.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </Field>
      </ReasonDialog>
      <ReasonDialog title="Reinstate" label="Why" confirm="Reinstate" open={dlg === "reinstate"} onClose={() => setDlg(null)} busy={reinstate.isPending} error={reinstate.error} onConfirm={(reason) => void reinstate.mutateAsync({ requisition_id: rid, application_id: aid, reason }).then(() => setDlg(null))} />
      <ReasonDialog title="Hold the candidate" label="Note (optional)" optional confirm="Hold" open={dlg === "hold"} onClose={() => setDlg(null)} busy={hold.isPending} error={hold.error} onConfirm={(t) => void hold.mutateAsync({ requisition_id: rid, application_id: aid, reason: hCode, reason_text: t || null }).then(() => setDlg(null))}>
        <Field label="Reason" required id="h-code" hint="A five-business-day review keeps nobody parked silently">
          <select id="h-code" value={hCode} onChange={(e) => setHCode(e.target.value as typeof hCode)}>
            <option value="stronger_candidates_first">Stronger candidates first</option>
            <option value="open_question">Open question</option>
            <option value="capacity_alternate">Alternate for capacity</option>
            <option value="other">Other</option>
          </select>
        </Field>
      </ReasonDialog>
      <ReasonDialog title="Release from hold" label="Note (optional)" optional confirm="Release" open={dlg === "release"} onClose={() => setDlg(null)} busy={release.isPending} error={release.error} onConfirm={(t) => void release.mutateAsync({ requisition_id: rid, application_id: aid, outcome: "release", reason_text: t || null }).then(() => setDlg(null))} />
      <ReasonDialog title="Pass from hold" label="Why" confirm="Pass" open={dlg === "pass"} onClose={() => setDlg(null)} busy={release.isPending} error={release.error} onConfirm={(t) => void release.mutateAsync({ requisition_id: rid, application_id: aid, outcome: "pass", reason_text: t }).then(() => setDlg(null))} />
      <ReasonDialog title="Not selected" label="Note (optional)" optional confirm="Record" open={dlg === "early"} onClose={() => setDlg(null)} busy={early.isPending} error={early.error} onConfirm={(t) => void early.mutateAsync({ requisition_id: rid, application_id: aid, reason_code: eCode, note: t || null }).then(() => setDlg(null))}>
        <Field label="Reason code" required id="e-code" hint="Booked rounds are cancelled; the disposition obligation opens now">
          <select id="e-code" value={eCode} onChange={(e) => setECode(e.target.value as ReasonCode)}>
            {REASON_CODES.map((k) => (
              <option key={k} value={k}>
                {REASON_LABEL[k]}
              </option>
            ))}
          </select>
        </Field>
      </ReasonDialog>
      <ReasonDialog title="Reverse the last decision" label="Why" confirm="Reverse" open={dlg === "reverse"} onClose={() => { setDlg(null); setTold(false); }} busy={reverse.isPending} error={reverse.error} onConfirm={(reason) => void reverse.mutateAsync({ requisition_id: rid, application_id: aid, reason, told_check: told }).then(() => { setDlg(null); setTold(false); })}>
        <label className="check">
          <input type="checkbox" checked={told} onChange={(e) => setTold(e.target.checked)} /> Nothing has reached the candidate from this application (required)
        </label>
      </ReasonDialog>
      <ReasonDialog title="Advance to the panel" label="Why" confirm="Advance" open={dlg === "adv_advance"} onClose={() => setDlg(null)} busy={advance.isPending} error={advance.error} onConfirm={(reason_text) => void advance.mutateAsync({ requisition_id: rid, application_id: aid, value: "advance", reason_text }).then(() => setDlg(null))}>
        <p className="muted">One planned interview per onsite round in use; the exercise step opens when the intake requires one. Refused when any submitted scorecard fired the two-2s rule.</p>
      </ReasonDialog>
      <ReasonDialog title="Hold after this round" label="Why" confirm="Hold" open={dlg === "adv_hold"} onClose={() => setDlg(null)} busy={advance.isPending} error={advance.error} onConfirm={(reason_text) => void advance.mutateAsync({ requisition_id: rid, application_id: aid, value: "hold", reason_text }).then(() => setDlg(null))} />
      <ReasonDialog title="Pass after this round" label="Why" confirm="Pass" open={dlg === "adv_pass"} onClose={() => setDlg(null)} busy={advance.isPending} error={advance.error} onConfirm={(reason_text) => void advance.mutateAsync({ requisition_id: rid, application_id: aid, value: "pass", reason_text, reason_code: "not_a_fit_for_level" }).then(() => setDlg(null))} />
      <ReasonDialog title="Log a message sent from the ATS" label="Note (optional)" optional confirm="Log" open={dlg === "message"} onClose={() => setDlg(null)} busy={logMsg.isPending} error={logMsg.error} onConfirm={(t) => void logMsg.mutateAsync({ requisition_id: rid, application_id: aid, kind: mKind, sent_at: new Date().toISOString(), reference: mRef || null, ...(t ? {} : {}) }).then(() => { setDlg(null); setMRef(""); })}>
        <Field label="Kind" required id="m-kind" hint="An invitation starts the two-business-day reply timer">
          <select id="m-kind" value={mKind} onChange={(e) => setMKind(e.target.value as typeof mKind)}>
            <option value="invitation">Invitation</option>
            <option value="itinerary">Itinerary</option>
            <option value="exercise_brief">Exercise brief</option>
            <option value="closing_note">Closing note</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="ATS message reference" id="m-ref">
          <input id="m-ref" value={mRef} onChange={(e) => setMRef(e.target.value)} />
        </Field>
      </ReasonDialog>
    </div>
  );
}


function ResumeSection({ rid, aid, candidate, application }: { rid: string; aid: string; candidate: string; application: Awaited<ReturnType<typeof candidateOpen>> }) {
  const { can, me } = useSession();
  const attach = useWrite((i: Parameters<typeof candidateAttachResume>[0], key) => candidateAttachResume(i, key), [["application", aid], ["candidates", rid]]);
  const [uploadKey, setUploadKey] = useState(0);
  const [file, setFile] = useState<FileUploadOutput | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploadPhase, setUploadPhase] = useState<FilePhase>("idle");
  if (!("rounds" in application) || application.projection !== "identity" && application.projection !== "review") return null;
  const versions = (application.resume_versions ?? []) as unknown as FileVersion[];
  const rows = versions.map((version) => ({ ...version, pinned: version.pinned && !!application.resume_pinned_at }));
  if (application.resume_file_id && !rows.some((r) => r.file_id === application.resume_file_id)) rows.unshift({ file_id: application.resume_file_id, version: application.resume_version, pinned: !!application.resume_pinned_at });
  const closed = application.state_archive === "archived" || ["archived", "closed_cancelled", "closed_filled"].includes(application.requisition_status);
  const readonly = closed || !!me?.view_as;
  return <section className="file-section">
    <h3 className="sub">Résumé</h3>
    <FileList label="Résumé versions" files={rows} />
    {application.resume_pinned_at && <p className="muted">Review version pinned {fmtDate(application.resume_pinned_at, true)}. Later uploads do not change the batch.</p>}
    {application.state_archive !== "archived" && application.projection === "identity" && can("candidate.attach_resume") && <>
      <FileField key={uploadKey} compactIdle label="Upload a résumé version" owner={{ kind: "resume", owner_type: "candidate", owner_id: candidate, requisition_id: rid }} accept={DOCUMENT_TYPES} disabled={readonly || attach.isPending} disabledReason={closed ? "This requisition is closed. Files are read-only." : me?.view_as ? "Viewing as another person — read-only." : undefined} onChange={(next) => { attach.reset(); setFile(next); }} onBusy={setBusy} onPhase={setUploadPhase} />
      {uploadPhase !== "idle" && !attach.isSuccess && <button className="btn" disabled={!file || busy || readonly || attach.isPending} onClick={() => file && void attach.mutateAsync({ requisition_id: rid, application_id: aid, resume_file_id: file.file_id }).then(() => { setFile(null); setUploadKey((key) => key + 1); }).catch(() => undefined)}>Save résumé version</button>}
      <Banner error={attach.error} />
      {attach.isSuccess && <Receipt>Résumé version saved. Earlier review versions stay on the record.</Receipt>}
    </>}
  </section>;
}

